import {
  collection, doc, getDoc, getDocs, setDoc, updateDoc, deleteDoc,
  query, orderBy, serverTimestamp, where, limit
} from 'firebase/firestore'
import { httpsCallable } from 'firebase/functions'
import { db, auth, functions } from './firebase'
import { docConId } from './docConId'
import { COLLECTIONS } from './constants'

// Helper: uid del usuario logueado actualmente (para audit trail).
// Si no hay sesión (ej. flujo de registro pre-auth), retorna null.
function _autorUid() {
  try { return auth?.currentUser?.uid || null } catch { return null }
}

// ═══════════════════════════════════════════════════════════════
// USUARIOS — CRUD
//
// Funciones de gestión de usuarios para el ABM de plataforma.
// El userDoc se crea normalmente desde AuthContext (login/registro),
// pero el admin puede crear manualmente, editar, suspender y eliminar.
// ═══════════════════════════════════════════════════════════════

const COL = COLLECTIONS.USUARIOS

export async function getUsuarios() {
  // V3.7.4 — limit defensivo + warning si trunca. Para Milbrands hoy ~30-50
  // usuarios; el plan tiene en cuenta crecimiento futuro. Si llegara a 500+
  // usuarios reales, el callsite necesita paginación real (startAfter) — el
  // warning del console alerta a tiempo.
  const QUERY_LIMIT = 500
  const snap = await getDocs(query(collection(db, COL), limit(QUERY_LIMIT)))
  const list = snap.docs.map(docConId)
  if (snap.size >= QUERY_LIMIT) {
    console.warn(`[getUsuarios] limit alcanzado (${QUERY_LIMIT}) — implementar paginación`)
  }
  list.sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''))
  return list
}

// ═══════════════════════════════════════════════════════════════
// DIRECTORIO DE DELEGACIÓN (report gAVXYLL7a28n1H90unzw)
//
// El selector "Delegar a" se llenaba con una query de LISTA sobre la raíz de
// `usuarios`, que las rules rechazan entera para todo perfil que no sea
// consultor: el combo quedaba vacío, en silencio. Ahora lo resuelve un
// callable server-side (`functions/usuariosParaDelegar.js`), que aplica el
// gate de tenant canónico y devuelve SÓLO id/nombre/email de quien tiene
// acceso a esa empresa.
//
// Devuelve el error en vez de tragarlo: "no se pudo cargar" y "no hay nadie"
// son dos cosas distintas y el usuario tiene que poder distinguirlas — que es
// exactamente lo que el reporte pedía.
// ═══════════════════════════════════════════════════════════════

/**
 * @param {string} empresaId — empresa de la tarea que se quiere delegar.
 * @returns {Promise<{usuarios: Array<{id,nombre,email}>, error: string|null}>}
 *   Nunca lanza: el llamador es un `<select>` y una excepción no le sirve.
 */
export async function getUsuariosParaDelegar(empresaId) {
  if (!empresaId) return { usuarios: [], error: 'Falta la empresa de la tarea' }
  try {
    // El binding se arma acá y no al importar el módulo, por el mismo molde que
    // `reuniones.js:87` (report du3xh8vn8Gmd8fmmf5aB): un `httpsCallable` en el
    // tope corre al IMPORTAR, así que cualquier test que mockee
    // `@core/lib/firebase` sin exportar `functions` ya no puede ni cargar el
    // módulo que lo alcance por transitividad — y eso se descubre lejos de acá,
    // en el archivo que sumó el import. Pasó con tres suites de productos y
    // agenteGeneral al cablear el hook del directorio en AuthContext.
    const res = await httpsCallable(functions, 'usuariosParaDelegar')({ empresaId })
    // `?? []`: el mock global de tests devuelve `{ data: {} }`, y un payload
    // sin `usuarios` no puede romper la pantalla.
    return { usuarios: res?.data?.usuarios ?? [], error: null }
  } catch (err) {
    return { usuarios: [], error: err?.message || 'No se pudo cargar el directorio' }
  }
}

export async function getUsuario(id) {
  const snap = await getDoc(doc(db, COL, id))
  return snap.exists() ? docConId(snap) : null
}

/**
 * Crear usuario manualmente (sin login previo).
 * El UID de Firebase Auth se asigna cuando el usuario haga su primer login.
 * Mientras tanto, el doc queda como "pre-registrado" con email como key.
 */
export async function crearUsuario(data) {
  // Usar email normalizado como ID temporal (se puede migrar después al UID real)
  const id = data.email.toLowerCase().replace(/[^a-z0-9@._-]/g, '')
  const ref = doc(db, COL, id)
  const autor = _autorUid()
  const payload = {
    nombre: data.nombre || '',
    email: data.email.toLowerCase().trim(),
    tipo: data.tipo || 'cliente',
    empresaIds: data.empresaId ? [data.empresaId] : [],
    estado: 'activo',
    creadoManual: true,
    creadoEn: serverTimestamp(),
    actualizadoEn: serverTimestamp(),
    creadoPor: autor,
    actualizadoPor: autor,
  }
  // v2 — modelo formal de roles (asignado opcional, se completa en B3-migration si falta)
  if (data.rolId) payload.rolId = data.rolId
  if (data.rolPlataformaId) payload.rolPlataformaId = data.rolPlataformaId
  await setDoc(ref, payload)
  return id
}

/**
 * Actualizar campos de un usuario existente.
 */
export async function actualizarUsuario(id, data) {
  const ref = doc(db, COL, id)
  // No permitir sobreescribir campos sensibles por accidente
  const { id: _id, creadoEn, ...safe } = data
  await updateDoc(ref, {
    ...safe,
    actualizadoEn: serverTimestamp(),
    actualizadoPor: _autorUid(),       // audit trail v2
  })
}

/**
 * Suspender usuario — bloquea acceso sin borrar el doc. El rol (rolId) se
 * conserva tal cual; al reactivar el usuario lo recupera.
 */
export async function suspenderUsuario(id) {
  const ref = doc(db, COL, id)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Usuario no encontrado')
  await updateDoc(ref, {
    estado: 'suspendido',
    actualizadoEn: serverTimestamp(),
  })
}

/**
 * Reactivar usuario suspendido.
 */
export async function reactivarUsuario(id) {
  const ref = doc(db, COL, id)
  const snap = await getDoc(ref)
  if (!snap.exists()) throw new Error('Usuario no encontrado')
  await updateDoc(ref, {
    estado: 'activo',
    actualizadoEn: serverTimestamp(),
  })
}

/**
 * Eliminar usuario (hard delete). Usar con precaución.
 */
export async function eliminarUsuario(id) {
  await deleteDoc(doc(db, COL, id))
}

/**
 * Buscar usuario por email.
 */
export async function buscarPorEmail(email) {
  const q = query(collection(db, COL), where('email', '==', email.toLowerCase().trim()))
  const snap = await getDocs(q)
  if (snap.empty) return null
  const d = snap.docs[0]
  return docConId(d)
}
